import * as React from 'react'

import { renderers } from '@markdoc/markdoc'
import type { HeadersFunction, LoaderFunctionArgs, MetaFunction } from '@remix-run/node'
import { Form, Link, useLoaderData, useSearchParams } from '@remix-run/react'

import Vimeo from '@/components/Vimeo'
import { useCurrent } from '@/current'
import { findLesson, findChapter, findCourse, findNextLesson, findPreviousLesson } from '@/models/course.server'
import { pageHelper } from '@/request'
import { formatLong } from '@/utils/date'
import { formatDuration, formatDurationISO8601 } from '@/utils/number'
import { isGooglebot } from '@/utils/user-agent'

type LoaderData = typeof loader

export async function loader({ params, request }: LoaderFunctionArgs) {
  const [course, chapter, lesson] = [
    findCourse(params.course as string),
    findChapter(params.course as string, params.chapter as string),
    findLesson(params.course as string, params.chapter as string, params.slug as string)
  ]

  if (!course || !chapter || !lesson) {
    throw new Response('Not Found', { status: 404 })
  }

  const { root } = pageHelper(request)
  const userAgent = request.headers.get('user-agent')
  const googlebot = isGooglebot(userAgent)

  return {
    lesson: lesson,
    course: course,
    chapter: chapter,
    nextLesson: findNextLesson(params.course as string, params.chapter as string, lesson.sequence),
    previousLesson: findPreviousLesson(params.course as string, params.chapter as string, lesson.sequence),
    root: root,
    googlebot: googlebot
  }
}

export const headers: HeadersFunction = ({ parentHeaders }) => ({
  'Cache-Control': parentHeaders.get('Cache-Control') ?? ''
})

export const meta: MetaFunction<LoaderData> = ({ data, params }) => {
  if (!data) return []

  const {
    course,
    chapter,
    lesson,
    lesson: { meta },
    root
  } = data

  const title = `${meta.title || lesson.title} — ${course.name} — PlanetScale`
  const description = meta.description || lesson.description
  const image = `${root}${meta.image}`

  return [
    { title },
    { name: 'description', content: description },
    { name: 'robots', content: meta.robots },
    { property: 'og:url', content: `https://planetscale.com${lesson.path}` },
    { property: 'og:type', content: 'website' },
    { property: 'og:title', content: title },
    { property: 'og:image', content: image },
    { property: 'og:description', content: description },
    { property: 'twitter:card', content: 'summary_large_image' },
    { property: 'twitter:site', content: '@planetscale' },
    { property: 'twitter:creator', content: '@planetscale' },
    { property: 'twitter:url', content: `https://planetscale.com${lesson.path}` },
    { property: 'twitter:title', content: title },
    { property: 'twitter:description', content: description },
    { property: 'twitter:image', content: image },
    { tagName: 'link', rel: 'canonical', href: `https://planetscale.com${lesson.path}` },
    { tagName: 'link', rel: 'preload', href: lesson.video.url, as: 'document' },
    {
      'script:ld+json': [
        {
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            {
              '@type': 'ListItem',
              position: 1,
              name: course.name,
              item: `https://planetscale.com/learn/courses/${params.course}`
            },
            {
              '@type': 'ListItem',
              position: 2,
              name: chapter.title,
              item: `https://planetscale.com/learn/courses/${params.course}/${params.chapter}`
            },
            {
              '@type': 'ListItem',
              position: 3,
              name: lesson.title
            }
          ]
        },
        {
          '@context': 'https://schema.org',
          '@type': 'Article',
          headline: lesson.title,
          articleBody: lesson.body_text,
          image: `https://planetscale.com${lesson.meta.image}`,
          dateCreated: lesson.createdAt,
          datePublished: lesson.createdAt,
          dateModified: lesson.updatedAt,
          author: {
            '@type': 'Organization',
            name: 'PlanetScale',
            url: 'planetscale.com'
          },
          video: {
            '@context': 'https://schema.org',
            '@type': 'VideoObject',
            name: lesson.title,
            description: lesson.description,
            duration: formatDurationISO8601(lesson.video.duration),
            embedUrl: lesson.video.url,
            image: `https://planetscale.com${lesson.meta.image}`,
            thumbnailUrl: `https://planetscale.com${lesson.meta.image}`,
            dateCreated: lesson.createdAt,
            uploadDate: lesson.createdAt,
            datePublished: lesson.createdAt,
            dateModified: lesson.updatedAt,
            author: {
              '@type': 'Organization',
              name: 'PlanetScale',
              url: 'planetscale.com'
            }
          }
        },
        {
          '@context': 'https://schema.org',
          '@type': 'Course',
          name: course.name,
          description: course.description,
          image: `https://planetscale.com${lesson.meta.image}`,
          datePublished: course.createdAt,
          inLanguage: 'en',
          provider: {
            '@type': 'Organization',
            name: 'PlanetScale',
            url: 'planetscale.com'
          },
          offers: {
            '@type': 'Offer',
            category: 'Free',
            price: '0',
            priceCurrency: 'USD'
          },
          hasCourseInstance: [
            {
              '@type': 'CourseInstance',
              courseMode: ['Online'],
              courseWorkload: formatDurationISO8601(course.duration),
              instructor: {
                '@type': 'Person',
                jobTitle: course.instructor.title,
                name: course.instructor.name
              }
            }
          ]
        }
      ]
    }
  ]
}

export default function CourseLesson() {
  const { chapter, lesson, nextLesson, previousLesson, googlebot } = useLoaderData<LoaderData>()
  const { userSignedIn } = useCurrent()

  const [searchParams] = useSearchParams()
  const autoplay = searchParams.get('autoplay') === '1'

  return (
    <div className='flex flex-col gap-5'>
      <main>
        <div className='relative mb-3 flex items-center text-center tracking-tight md:hidden'>
          {previousLesson && (
            <Link
              className='absolute flex items-center gap-[4px] border p-1 text-primary md:items-start'
              prefetch='intent'
              rel='prev'
              to={previousLesson.path}
            >
              &larr;
              <span className='text-secondary'>
                <span aria-hidden='true'>Previous</span>
                <span className='sr-only'>{previousLesson.title}</span>
              </span>
            </Link>
          )}

          <div className='flex w-full flex-auto items-baseline justify-center gap-[4px] px-9'>
            <span aria-hidden='true' className='font-light tracking-tight text-secondary sm:text-base lg:hidden'>
              {chapter.sequence}.{lesson.sequence}
            </span>
            <h1 className='inline font-semibold text-primary'>{lesson.title}</h1>
          </div>

          {nextLesson && (
            <Link
              className='absolute right-0 flex items-center gap-[4px] border p-1 text-primary md:items-start'
              prefetch='intent'
              rel='next'
              to={nextLesson.path}
            >
              <span className='text-secondary'>
                <span aria-hidden='true'>Next</span>
                <span className='sr-only'>{nextLesson.title}</span>
              </span>
              &rarr;
            </Link>
          )}
        </div>

        <div className='relative flex aspect-video w-full items-center justify-center bg-secondary p-[3px] lg:p-2'>
          {autoplay ? (
            <Vimeo
              autoplay
              className='aspect-video h-full w-full bg-primary'
              src={lesson.video.url}
              title={lesson.title}
            />
          ) : googlebot ? (
            <iframe className='aspect-video h-full w-full bg-primary' src={lesson.video.url} title={lesson.title} />
          ) : (
            <Form className='aspect-video h-full w-full bg-primary' method='get'>
              <button
                name='autoplay'
                className='group flex h-[inherit] w-[inherit] flex-col items-center justify-center gap-2 px-sm font-bold text-primary md:px-4'
                type='submit'
                value='1'
              >
                <div className='z-1 flex h-8 w-8 items-center justify-center border border-orange-500 p-sm text-[32px] leading-none text-orange transition hover:bg-secondary'>
                  <span className='h-[36px]'>▶︎</span>
                </div>
                <span className='transition lg:max-w-[66%]'>{lesson.title}</span>
              </button>
            </Form>
          )}
        </div>

        {!userSignedIn && !googlebot && (
          <div className='mt-3'>
            <span className='hidden text-primary lg:inline'>Want an easy-to-use, performant, MySQL database? </span>
            <a href='https://auth.planetscale.com/sign-up'>Try PlanetScale now</a>
          </div>
        )}
      </main>

      <div className='container max-w-[1200px] gap-2 px-0'>
        <div className='flex flex-col gap-5 overflow-hidden'>
          <header>
            <span className='text-orange'>
              {chapter.sequence}.{lesson.sequence}
            </span>{' '}
            <span className='inline font-semibold text-primary'>{lesson.title}</span>
          </header>

          <aside className='flex flex-col-reverse gap-5'>
            <div className='block max-w-none text-secondary'>
              {renderers.react(lesson.body, React, {
                components: {}
              })}
            </div>

            <div className='flex flex-col gap-3 lg:flex-row'>
              <div className='flex w-full flex-col flex-wrap justify-between gap-2 border p-3'>
                <div className='space-y-1'>
                  <h2 className='font-semibold text-primary'>About this lesson</h2>
                  <p className='text-secondary'>{lesson.description}</p>
                </div>

                <div className='flex w-full flex-wrap gap-1 text-secondary'>
                  <span>{formatDuration(lesson.video.duration)}</span>
                  &middot;
                  <div className='flex flex-wrap items-center gap-2'>
                    <time dateTime={lesson.createdAt}>{formatLong(lesson.createdAt)}</time>
                    <div className='border border-contrast px-sm text-center font-mono font-medium tracking-widest text-secondary'>
                      <span className='sr-only'>Closed captioned</span>
                      <span aria-hidden='true' className='select-none'>
                        CC
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </aside>

          <div className='mb-2'>
            <p className='text-primary'>
              Feedback or questions? Reach out to our team at{' '}
              <a href='mailto:education@planetscale.com'>education@planetscale.com</a>.
            </p>
          </div>

          {(nextLesson || previousLesson) && (
            <nav className='flex flex-col items-center gap-2 overflow-hidden md:flex-row xl:gap-4'>
              <h2 className='sr-only'>Learn more about {chapter.title}</h2>
              <div className='flex h-full w-full'>
                {previousLesson && (
                  <Link
                    className='bordert group flex h-full w-full flex-col flex-wrap border p-3 text-start text-primary transition hover:bg-secondary'
                    prefetch='intent'
                    rel='prev'
                    to={previousLesson.path}
                  >
                    <h3 className='mb-0 text-orange'>
                      <span aria-hidden='true'>&larr;</span> Previous lesson
                    </h3>
                    <span className='text-secondary'>
                      {chapter.sequence}.{previousLesson.sequence}{' '}
                      <span className='font-semibold text-primary'>{previousLesson.title}</span>
                    </span>
                  </Link>
                )}
              </div>

              <div className='flex h-full w-full'>
                {nextLesson && (
                  <Link
                    className='group flex h-full w-full flex-col flex-wrap items-end border p-3 text-right text-primary transition hover:bg-secondary'
                    prefetch='intent'
                    rel='next'
                    to={nextLesson.path}
                  >
                    <h3 className='mb-0 text-orange'>
                      Next lesson<span aria-hidden='true'> &rarr;</span>
                    </h3>
                    <span className='text-primary'>
                      {chapter.sequence}.{nextLesson.sequence}{' '}
                      <span className='font-semibold text-primary'>{nextLesson.title}</span>
                    </span>
                  </Link>
                )}
              </div>
            </nav>
          )}
        </div>
      </div>
    </div>
  )
}
